import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import AssetsTable from '../components/assets/AssetsTable';
import AssetModal from '../components/assets/AssetModal.jsx';
import useAdminAssets from '../hooks/useAdminAssets.js';
import FilterFieldSelect from '../components/forms/FilterFieldSelect';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle.js';
import { OPEN_VIEW_PARAM, ASSETS_OPEN } from '../utils/openViewParams';

const ASSET_TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
];

export default function AssetsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const { data: assets, createAsset } = useAdminAssets(searchParamsKey);
  const navigate = useNavigate();
  useDocumentTitle('Assets – Maintella');

  const [showNewModal, setShowNewModal] = useState(false);

  // Redirect to first asset when opening a sub-view (e.g. task-report) so the detail page can open the modal
  useEffect(() => {
    const open = searchParams.get(OPEN_VIEW_PARAM);
    if (open === ASSETS_OPEN.TASK_REPORT && assets?.length > 0) {
      const firstId = assets[0].id;
      navigate(`/admin/assets/${firstId}?${OPEN_VIEW_PARAM}=${encodeURIComponent(open)}`, { replace: true });
    }
  }, [searchParams, assets, navigate]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const make = searchParams.get('make') || 'all';
  const model = searchParams.get('model') || 'all';
  const assetType = searchParams.get('asset_type') || 'all';

  const makeOptions = useMemo(() => {
    const values = Array.from(new Set((assets || []).map((a) => (a.asset_make || '').trim()).filter(Boolean))).sort();
    if (make !== 'all' && !values.includes(make)) values.unshift(make);
    return [{ value: 'all', label: 'All makes' }, ...values.map((v) => ({ value: v, label: v }))];
  }, [assets, make]);

  const modelOptions = useMemo(() => {
    const values = Array.from(new Set((assets || []).map((a) => (a.asset_model || '').trim()).filter(Boolean))).sort();
    if (model !== 'all' && !values.includes(model)) values.unshift(model);
    return [{ value: 'all', label: 'All models' }, ...values.map((v) => ({ value: v, label: v }))];
  }, [assets, model]);

  const setMakeFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('make');
    else next.set('make', value);
    setSearchParams(next, { replace: true });
  };

  const setModelFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('model');
    else next.set('model', value);
    setSearchParams(next, { replace: true });
  };

  const setAssetTypeFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('asset_type');
    else next.set('asset_type', value);
    setSearchParams(next, { replace: true });
  };

  const assetFilterExtras = useMemo(
    () => (
      <>
        <FilterFieldSelect
          id="assets-filter-make"
          label="Make"
          value={make}
          onChange={(e) => setMakeFilter(e.target.value)}
          options={makeOptions}
        />
        <FilterFieldSelect
          id="assets-filter-model"
          label="Model"
          value={model}
          onChange={(e) => setModelFilter(e.target.value)}
          options={modelOptions}
        />
        <FilterFieldSelect
          id="assets-filter-type"
          label="Type"
          value={assetType}
          onChange={(e) => setAssetTypeFilter(e.target.value)}
          options={ASSET_TYPE_OPTIONS}
        />
      </>
    ),
    // filter-page pattern: searchParamsKey covers URL setter stability
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [make, model, assetType, makeOptions, modelOptions, searchParamsKey]
  );

  const handleCreateAsset = async (assetData) => {
    setSaving(true);
    setError(null);
    try {
      const created = await createAsset(assetData);
      setShowNewModal(false);
      // Navigate to the new asset's detail page
      navigate(`/admin/assets/${created.id}`);
    } catch (err) {
      setError(err.message || 'Failed to create asset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ObjectTopBar
        title="Assets"
        primaryBtn={{
          btnText: 'New Asset',
          btnIcon: 'bi bi-plus-lg',
          variant: 'primary',
          onClick: () => setShowNewModal(true),
        }}
      />
      <div className="container py-4">
        <AssetsTable items={assets} filterExtras={assetFilterExtras} />
      </div>

      <AssetModal
        show={showNewModal}
        onClose={() => {
          setShowNewModal(false);
          setError(null);
        }}
        asset={null}
        onSave={handleCreateAsset}
        saving={saving}
        error={error}
      />
    </>
  );
}